// A single-line URL input with an "open link" affordance: the trailing
// button opens the typed URL in a new tab (disabled until the value looks
// like an openable link).

import type { CSSProperties } from 'react'
import { ExternalLink } from 'lucide-react'

interface Props {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  disabled?: boolean
  maxLength?: number
  style?: CSSProperties
  'data-livepreview-field'?: string
}

// Accept http(s), mailto, tel, or a protocol-relative/relative path. Used
// only to decide whether the open button is enabled — never to block input.
export function isOpenableUrl(v: string): boolean {
  const s = (v || '').trim()
  if (!s) return false
  return /^(https?:\/\/|mailto:|tel:|\/\/|\/)/i.test(s) || /^[\w.-]+\.[a-z]{2,}(\/|$)/i.test(s)
}

function normalizeHref(v: string): string {
  const s = v.trim()
  // Bare domain ("example.com/x") → assume https so the browser doesn't
  // treat it as a relative path.
  if (/^[\w.-]+\.[a-z]{2,}(\/|$)/i.test(s) && !/^https?:\/\//i.test(s)) {
    return `https://${s}`
  }
  return s
}

export default function UrlField({
  value, onChange, placeholder, disabled, maxLength, style, ...rest
}: Props) {
  const openable = isOpenableUrl(value)
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <input
        type="text"
        inputMode="url"
        value={value ?? ''}
        placeholder={placeholder || 'https://…'}
        disabled={disabled}
        maxLength={maxLength}
        onChange={e => onChange(e.target.value)}
        style={{ flex: 1, ...style }}
        {...rest}
      />
      <a
        href={openable ? normalizeHref(value) : undefined}
        target="_blank"
        rel="noopener noreferrer"
        title={openable ? 'Open link in new tab' : 'Enter a URL to open it'}
        onClick={e => { if (!openable) e.preventDefault() }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 10px',
          border: '1px solid var(--color-border)',
          borderRadius: 4,
          color: openable ? 'var(--color-accent)' : 'var(--color-text-secondary)',
          background: 'var(--color-surface-2, var(--color-bg-secondary))',
          cursor: openable ? 'pointer' : 'not-allowed',
          textDecoration: 'none',
          opacity: openable ? 1 : 0.5,
        }}
      >
        <ExternalLink size={15} />
      </a>
    </div>
  )
}
